import React from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import type { Product } from '../../types/products.types';
import { Button, Table } from '@shared/components';
import { formatRejectionReason } from '../../utils/formatRejectionReason';

interface Props {
    data: Product[];
    onSelectProduct?: (product: Product) => void;
}

export const ProductTable: React.FC<Props> = ({ data, onSelectProduct }) => {
    const hasEligibilityData = data.some((p) => p.eligibility_result !== undefined);

    const columns: ColumnDef<Product>[] = [
        {
            accessorKey: 'name',
            header: () => 'Product Name',
            cell: (info) => <span className="font-semibold">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'description',
            header: () => 'Description',
            cell: (info) => (
                <span className="text-sm text-mp-muted">
                    {info.getValue() as string}
                </span>
            ),
        },
        {
            accessorKey: 'features',
            header: () => 'Features',
            cell: (info) => (
                <ul className="list-disc list-inside text-sm text-mp-foreground space-y-1">
                    {(info.getValue() as string[]).map((feature, index) => (
                        <li key={index}>{feature}</li>
                    ))}
                </ul>
            ),
        },
        {
            accessorKey: 'price',
            header: () => 'Price From',
            cell: (info) => (
                <span className="font-bold text-mp-success">
                    {info.getValue() as string}
                </span>
            ),
        },
        ...(hasEligibilityData
            ? [
                  {
                      id: 'eligibility',
                      header: () => 'Eligibility',
                      cell: (info: { row: { original: Product } }) => {
                          const result = info.row.original.eligibility_result;
                          if (!result) return null;
                          if (result.passed) {
                              return (
                                  <span className="text-mp-success" title="Eligible">
                                      ✓
                                  </span>
                              );
                          }
                          const failed = result.evaluations.filter((e) => e.result === 'fail');
                          const tooltip = failed.map(formatRejectionReason).join('\n');
                          return (
                              <span
                                  className="text-mp-warning cursor-help"
                                  title={tooltip}
                              >
                                  ⚠
                              </span>
                          );
                      },
                      enableSorting: false,
                  },
              ]
            : []),
        {
            id: 'select',
            header: () => 'Select',
            cell: (info) => (
                <Button
                    size="xs"
                    onClick={() => onSelectProduct?.(info.row.original)}
                    className="whitespace-nowrap"
                >
                    Select
                </Button>
            ),
            enableSorting: false,
        },
    ];

    return <Table<Product> data={data} columns={columns} enableSorting={true} />;
};
